<script setup lang="ts">
import { computed } from "vue";
import { useStore } from "vuex";
import { addSizeToDataLayer } from "@/functions/analytics";
import DimensionInput from "@/components/UI/DimensionInput.vue";
import ShapeInputFieldsWrapper from "@/components/UI/ShapeInputFieldsWrapper.vue";
import {Props} from "@/components/ShapeType.vue";

const store = useStore();
const { isCutout, action, prefix } = defineProps<Props>();

const dimensions = computed(() => store.getters.getMeasurements( 'right-angled-triangle-mirrored', isCutout ) );
const dimensionLabel = computed(() => store.getters.dimensionLabel);
const dimensionMaxLabel = computed(() => store.getters.getMaxLabelForDimension);
const isDimensionValid = computed(() => store.getters.isShapeValid( isCutout ) );

const widthTop = computed({
	get: () => dimensions.value.width_top,
	set: (value: string) => {
		const numericValue = value;
		const widthBottom = dimensions.value.width_bottom;

		store.dispatch(action, {
			dimension: "width_top",
			value: value,
		});
		store.dispatch(action, {
			dimension: "offset_left",
			value: ( ( parseFloat(widthBottom) - parseFloat(numericValue) ) / 2 ).toFixed( 1 ),
		});
	},
});

const widthBottom = computed({
	get: () => dimensions.value.width_bottom,
	set: (value: string) => {
		const numericValue = value;
		const widthTop = dimensions.value.width_top;

		store.dispatch(action, {
			dimension: "width_bottom",
			value: value,
		});
		store.dispatch(action, {
			dimension: "offset_left",
			value: ( ( parseFloat(numericValue) - parseFloat(widthTop) ) / 2 ).toFixed( 1 ),
		});
	},
});

const height = computed({
	get: () => dimensions.value.height,
	set: (value: string) => {
		store.dispatch(action, {
			dimension: "height",
			value: value,
		});
	},
});

const offsetLeft = computed({
	get: () => dimensions.value.offset_left,
	set: (value: string) => {
		store.dispatch(action, {
			dimension: "offset_left",
			value: value,
		});
	},
});
</script>

<template>
	<shape-input-fields-wrapper>
		<dimension-input
			inputRef="trapezoid-width_top"
			:id="`${prefix}measurements-trapezoid-width_top`"
			:name="`${prefix}measurements[dimensions][width_top]`"
			unit="cm"
			:label="dimensionLabel('width_top', 'trapezoid')"
			:instructions="isCutout ? '' : dimensionMaxLabel('width_top')"
			:invalid="!isDimensionValid(`${prefix}trapezoid-width_top`)"
			:onBlur="() => addSizeToDataLayer('width_top', +widthTop, 'trapezoid')"
			v-model="widthTop"
		/>

		<dimension-input
			inputRef="trapezoid-width_bottom"
			:id="`${prefix}measurements-trapezoid-width_bottom`"
			:name="`${prefix}measurements[dimensions][width_bottom]`"
			unit="cm"
			:label="dimensionLabel('width_bottom', 'trapezoid')"
			:instructions="isCutout ? '' : dimensionMaxLabel('width_bottom')"
			:invalid="!isDimensionValid(`${prefix}trapezoid-width_bottom`)"
			:onBlur="() => addSizeToDataLayer('width_bottom', +widthBottom, 'trapezoid')"
			v-model="widthBottom"
		/>

		<dimension-input
			inputRef="trapezoid-height"
			:id="`${prefix}measurements-trapezoid-height`"
			:name="`${prefix}measurements[dimensions][height]`"
			unit="cm"
			:label="dimensionLabel('height', 'trapezoid')"
			:instructions="isCutout ? '' : dimensionMaxLabel('height')"
			:invalid="!isDimensionValid(`${prefix}trapezoid-height`)"
			:onBlur="() => addSizeToDataLayer('height', +height, 'trapezoid')"
			v-model="height"
		/>

		<dimension-input
			inputRef="trapezoid-offset_left"
			:id="`${prefix}measurements-trapezoid-offset_left`"
			:name="`${prefix}measurements[dimensions][offset_left]`"
			unit="cm"
			hidden
			:label="dimensionLabel('offset_left', 'trapezoid')"
			:instructions="isCutout ? '' : dimensionMaxLabel('offset_left')"
			:invalid="!isDimensionValid(`${prefix}trapezoid-offset_left`)"
			:onBlur="() => addSizeToDataLayer('offset_left', +offsetLeft, 'trapezoid')"
			v-model="offsetLeft"
		/>
	</shape-input-fields-wrapper>
</template>
